import PropTypes from "prop-types";
import React from "react";
import { Script } from "gatsby";

import striptags from "striptags";
import { isArray } from "lodash";

import useSiteMetadata from "~/hooks/useSiteMetadata";

import ErrorBoundary from "~/components/ErrorBoundary";

const createSeoSchemas = (
    siteUrl,
    logo,
    breadcrumbStructuredData,
    seoSchema,
) => {
    const seoSchemas = [
        `{
            "@context": "https://schema.org/",
            "@type": "Organization",
            "url": "${siteUrl}",
            "logo": "${logo}"
        }`,
    ];

    if (breadcrumbStructuredData) {
        seoSchemas.push(
            `{
                "@context": "https://schema.org/",
                "@type": "BreadcrumbList",
                "itemListElement": [${breadcrumbStructuredData}]
             }`,
        );
    }

    if (seoSchema) {
        if (isArray(seoSchema)) {
            seoSchema.forEach((schema) => {
                if (schema) {
                    seoSchemas.push(schema);
                }
            });
        } else {
            seoSchemas.push(seoSchema);
        }
    }

    return seoSchemas;
};

const Seo = ({
    pageTitle = "",
    seo = {},
    fallbackDescription = "",
    featuredImage = {},
    breadcrumbs = [],
    seoSchema = "",
    location = { pathname: "" },
    canonicalUrl = "",
}) => {
    const { siteUrl, companyLogo } = useSiteMetadata();

    let breadcrumbStructuredData = "";

    if (breadcrumbs?.length) {
        breadcrumbStructuredData = breadcrumbs.map((crumb, index) => {
            if (breadcrumbs.length - 1 === index) {
                return `{
                    "@type": "ListItem",
                    "position": "${index + 1}",
                    "name": "${crumb.crumbLabel}"
                }`;
            }

            return `{
                "@type": "ListItem",
                "position": "${index + 1}",
                "name": "${crumb.crumbLabel}",
                "item": "${siteUrl}${crumb.pathname}"
            }`;
        });
    }

    const seoInfo = seo ? seo : {};
    const seoDescription = seoInfo.metaDesc
        ? seoInfo.metaDesc
        : striptags(fallbackDescription);
    const seoSchemas = createSeoSchemas(
        siteUrl,
        companyLogo,
        breadcrumbStructuredData,
        seoSchema,
    );

    const url = canonicalUrl || `${siteUrl}${location.pathname}`;
    const title = seoInfo.title ? seoInfo.title : pageTitle;

    const twitterDescription = seoInfo.twitterDescription
        ? seoInfo.twitterDescription
        : seoDescription;
    const opengraphDescription = seoInfo.opengraphDescription
        ? seoInfo.opengraphDescription
        : seoDescription;
    const twitterImage = seoInfo.twitterImage?.sourceUrl
        ? seoInfo.twitterImage.sourceUrl
        : featuredImage?.node?.sourceUrl;
    const openGraphImage = seoInfo.opengraphImage?.sourceUrl
        ? seoInfo.opengraphImage.sourceUrl
        : featuredImage?.node?.sourceUrl;

    return (
        <ErrorBoundary>
            <title>{title}</title>
            <link id="canonical" rel="canonical" href={url} />
            {seoDescription ? (
                <meta name="description" content={seoDescription} />
            ) : null}

            <meta property="og:url" content={url} />
            {seoInfo.opengraphType ? (
                <meta property="og:type" content={seoInfo.opengraphType} />
            ) : null}
            <meta
                property="og:title"
                content={
                    seoInfo.opengraphTitle ? seoInfo.opengraphTitle : title
                }
            />
            {opengraphDescription ? (
                <meta
                    property="og:description"
                    content={opengraphDescription}
                />
            ) : null}
            {openGraphImage ? (
                <meta property="og:image" content={openGraphImage} />
            ) : null}

            <meta name="twitter:card" content="summary_large_image" />
            <meta
                name="twitter:title"
                content={seoInfo.twitterTitle ? seoInfo.twitterTitle : title}
            />
            {twitterDescription ? (
                <meta name="twitter:description" content={twitterDescription} />
            ) : null}
            {twitterImage ? (
                <meta name="twitter:image" content={twitterImage} />
            ) : null}
            <Script id="structuredDataSchemas" type="application/ld+json">
                {`[${seoSchemas}]`}
            </Script>
        </ErrorBoundary>
    );
};

Seo.propTypes = {
    pageTitle: PropTypes.string,
    description: PropTypes.string,
    seo: PropTypes.shape({}),
    fallbackDescription: PropTypes.string,
    featuredImage: PropTypes.shape({}),
    breadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            crumbLabel: PropTypes.string.isRequired,
            pathname: PropTypes.string,
        }).isRequired,
    ),
    seoSchema: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }),
    canonicalUrl: PropTypes.string,
};

export default Seo;
